/**
 * The visit: one browser tab's run of the feed. Minted once per tab (sessionStorage), so a
 * reload continues the same visit and a new tab starts another.
 *
 * The ranker records visit_id, visit_ordinal and feed_position on every row so a later
 * version can separate "this card is good" from "this card was first": position bias cannot
 * be un-confounded after the fact without them. v1 ranking does not read them; they exist
 * so that v2 can.
 */
const KEY_ID = 'blitz.visit.id';
const KEY_ORDINAL = 'blitz.visit.ordinal';

let memoryId: string | null = null;
let memoryOrdinal = 0;

function store(): Storage | null {
  try {
    return sessionStorage;
  } catch {
    return null; // sandboxed or storage disabled: the visit lives in memory instead
  }
}

function mint(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** This tab's visit id. Stable across reloads; distinct per tab. */
export function visitId(): string {
  const s = store();
  const kept = s?.getItem(KEY_ID) ?? memoryId;
  if (kept) return kept;
  const id = mint();
  memoryId = id;
  try {
    s?.setItem(KEY_ID, id);
  } catch {
    /* quota or private mode: memory copy suffices for this tab */
  }
  return id;
}

/** The ordinal of the next card session in this visit: 1 for the first card, 2 for the second… */
export function nextVisitOrdinal(): number {
  const s = store();
  const prev = Number(s?.getItem(KEY_ORDINAL) ?? memoryOrdinal) || 0;
  const n = prev + 1;
  memoryOrdinal = n;
  try {
    s?.setItem(KEY_ORDINAL, String(n));
  } catch {
    /* as above */
  }
  return n;
}

/** Test hook: forget this tab's visit. */
export function resetVisit(): void {
  memoryId = null;
  memoryOrdinal = 0;
  try {
    store()?.removeItem(KEY_ID);
    store()?.removeItem(KEY_ORDINAL);
  } catch {
    /* nothing to clear */
  }
}
